// src/components/DocumentModal.tsx: full-screen viewer for a generated HTML
// document (agreement or invoice).
//
// The document is rendered through an iframe srcDoc rather than a link to a
// stored file. A signed storage URL is served as a download by the browser, so
// the reader never sees the document; srcDoc renders it inline and, because a
// srcDoc iframe inherits the parent origin, contentWindow.print() is available
// for Save as PDF.
import React, { useEffect, useRef } from 'react';

interface DocumentModalProps {
  html: string;
  title: string;
  onClose: () => void;
}

const toolbarBtn: React.CSSProperties = {
  background: 'rgba(255,255,255,0.1)',
  border: '1px solid rgba(255,255,255,0.2)',
  color: '#FFFFFF',
  padding: '6px 16px',
  borderRadius: '3px',
  cursor: 'pointer',
  fontSize: '12px',
  fontFamily: 'system-ui',
};

export default function DocumentModal({ html, title, onClose }: DocumentModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Escape closes, and the page behind must not scroll while the overlay is up.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.focus();
    win.print();
  };

  const handleDownload = () => {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '-').toLowerCase()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Toolbar */}
      <div style={{
        background: '#0A1628',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexShrink: 0,
      }}>
        <span style={{
          color: '#FFFFFF',
          fontFamily: 'Georgia, serif',
          fontSize: '15px',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{title}</span>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
          <button onClick={handlePrint} style={toolbarBtn}>Print / Save PDF</button>
          <button onClick={handleDownload} style={toolbarBtn}>Download</button>
          <button
            onClick={onClose}
            aria-label="Close document"
            style={{
              background: '#C9A84C',
              border: 'none',
              color: '#0A1628',
              padding: '6px 16px',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 700,
              fontFamily: 'system-ui',
            }}
          >Close</button>
        </div>
      </div>

      {/* Document */}
      <iframe
        ref={iframeRef}
        srcDoc={html}
        style={{ flex: 1, border: 'none', background: '#FFFFFF', width: '100%' }}
        title={title}
      />
    </div>
  );
}
